// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Word correction management UI only
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent component
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Word Correction Management
 *
 * Manage word correction mappings for all supported languages.
 * Users can paste raw Whisper transcription text, generate corrections using AI,
 * and edit/delete mappings.
 * Requires Super Admin (API enforces).
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoFixHigh as AutoFixHighIcon,
  Add as AddIcon,
  Spellcheck as SpellcheckIcon,
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../../config/api';
import ConfirmDialog from '../common/ConfirmDialog';
import PageSectionHeader from '../common/PageSectionHeader';

// Supported languages (matching backend SUPPORTED_LANGUAGES)
const SUPPORTED_LANGUAGES = {
  th: { name: 'Thai', native: 'ไทย' },
  en: { name: 'English', native: 'English' },
  zh: { name: 'Chinese', native: '中文' },
  cn: { name: 'Chinese', native: '中文' },
  ar: { name: 'Arabic', native: 'العربية' },
  ja: { name: 'Japanese', native: '日本語' },
  ko: { name: 'Korean', native: '한국어' },
  ru: { name: 'Russian', native: 'Русский' },
  yue: { name: 'Cantonese', native: '粵語' },
  vi: { name: 'Vietnamese', native: 'Tiếng Việt' },
  tl: { name: 'Tagalog', native: 'Tagalog' },
  id: { name: 'Bahasa', native: 'Bahasa Indonesia' },
};

const WordCorrectionManagement = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('th');
  const [rawText, setRawText] = useState('');
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, correction: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, incorrect: '' });
  const [editForm, setEditForm] = useState({ incorrect: '', correct: '' });

  // Load corrections when language changes
  useEffect(() => {
    loadCorrections();
  }, [selectedLanguage]);

  const loadCorrections = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`${API_ENDPOINTS.WORD_CORRECTION}/${selectedLanguage}`);
      if (res.data?.success && Array.isArray(res.data.data)) {
        setCorrections(res.data.data);
      } else {
        setCorrections([]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load word corrections');
      setCorrections([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!rawText.trim()) {
      setError('Please paste raw transcription text');
      return;
    }

    setGenerating(true);
    setError(null);
    try {
      const res = await apiClient.post(API_ENDPOINTS.WORD_CORRECTION_GENERATE, {
        text: rawText,
        language: selectedLanguage,
      });
      
      if (res.data?.success && Array.isArray(res.data.data)) {
        const generated = res.data.data;
        if (generated.length > 0) {
          // Add generated corrections
          await handleAddCorrections(generated);
          setRawText(''); // Clear text area after successful generation
          setSuccess(`Generated ${generated.length} word corrections`);
        } else {
          setSuccess('No corrections found in the text');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate word corrections');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddCorrections = async (newCorrections) => {
    try {
      const res = await apiClient.post(
        `${API_ENDPOINTS.WORD_CORRECTION}/${selectedLanguage}`,
        { corrections: newCorrections }
      );
      
      if (res.data?.success) {
        await loadCorrections();
        return true;
      }
      return false;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add word corrections');
      return false;
    }
  };

  const handleEdit = (correction) => {
    setEditForm({
      incorrect: correction.incorrect,
      correct: correction.correct,
    });
    setEditDialog({ open: true, correction });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.correction) return;
    
    try {
      const res = await apiClient.put(
        `${API_ENDPOINTS.WORD_CORRECTION}/${selectedLanguage}`,
        {
          old_incorrect: editDialog.correction.incorrect,
          new_correction: {
            incorrect: editForm.incorrect,
            correct: editForm.correct,
          },
        }
      );
      
      if (res.data?.success) {
        await loadCorrections();
        setEditDialog({ open: false, correction: null });
        setSuccess('Word correction updated successfully');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update word correction');
    }
  };

  const handleDelete = async () => {
    try {
      const res = await apiClient.delete(
        `${API_ENDPOINTS.WORD_CORRECTION}/${selectedLanguage}/${encodeURIComponent(deleteDialog.incorrect)}`
      );
      
      if (res.data?.success) {
        await loadCorrections();
        setDeleteDialog({ open: false, incorrect: '' });
        setSuccess('Word correction deleted successfully');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete word correction');
    }
  };

  const handleAddNew = () => {
    setEditForm({ incorrect: '', correct: '' });
    setEditDialog({ open: true, correction: null });
  };

  const handleSaveNew = async () => {
    if (!editForm.incorrect || !editForm.correct) {
      setError('Both incorrect and correct words are required');
      return;
    }

    await handleAddCorrections([{
      incorrect: editForm.incorrect,
      correct: editForm.correct,
    }]);
    
    setEditDialog({ open: false, correction: null });
    setEditForm({ incorrect: '', correct: '' });
    setSuccess('Word correction added successfully');
  };

  return (
    <Box>
      <PageSectionHeader
        icon={<SpellcheckIcon />}
        title="Word Correction"
        description="Manage word correction mappings for each language. Paste raw Whisper transcription text to generate corrections automatically using AI."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Language Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Language</InputLabel>
            <Select
              value={selectedLanguage}
              label="Language"
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              {Object.entries(SUPPORTED_LANGUAGES).map(([code, info]) => (
                <MenuItem key={code} value={code}>
                  {info.native} ({info.name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Text Area for Raw Transcription */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Raw Transcription Text (From Whisper)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Paste raw Whisper transcription text here..."
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            onClick={handleGenerate}
            disabled={generating || !rawText.trim()}
            sx={{ backgroundColor: '#13213c', '&:hover': { backgroundColor: '#2a3f5f' } }}
          >
            {generating ? 'Generating...' : 'Generate Mappings'}
          </Button>
          {generating && (
            <CircularProgress size={20} sx={{ ml: 2 }} />
          )}
        </CardContent>
      </Card>

      {/* Word Corrections Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Word Corrections ({SUPPORTED_LANGUAGES[selectedLanguage]?.native || selectedLanguage})
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
            >
              Add New
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : corrections.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No word corrections found. Generate mappings from text or add manually.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Incorrect Word</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Correct Word</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {corrections.map((correction, index) => (
                  <TableRow key={index}>
                    <TableCell>{correction.incorrect}</TableCell>
                    <TableCell>{correction.correct}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(correction)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteDialog({ open: true, incorrect: correction.incorrect })}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit/Add Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, correction: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editDialog.correction ? 'Edit Word Correction' : 'Add Word Correction'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Incorrect Word"
            value={editForm.incorrect}
            onChange={(e) => setEditForm({ ...editForm, incorrect: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Correct Word"
            value={editForm.correct}
            onChange={(e) => setEditForm({ ...editForm, correct: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, correction: null })}>
            Cancel
          </Button>
          <Button
            onClick={editDialog.correction ? handleSaveEdit : handleSaveNew}
            variant="contained"
            sx={{ backgroundColor: '#13213c', '&:hover': { backgroundColor: '#2a3f5f' } }}
          >
            {editDialog.correction ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, incorrect: '' })}
        onConfirm={handleDelete}
        title="Delete Word Correction"
        message={`Are you sure you want to delete the correction for "${deleteDialog.incorrect}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        confirmColor="error"
      />
    </Box>
  );
};

export default WordCorrectionManagement;
